import { useState } from "react";

import { ChevronDown, ChevronUp } from "lucide-react";

import { Badge } from "@components/Badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@components/Collapsible";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { useWebSocketStore } from "@storage/global/useWebSocketStore";
import { useBroadcastStatusDataQuery } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import { usePrivateRequestListQuery } from "@storage/queries/privateRequest";

import { cn } from "@lib/utils";

import { ICON_PATHS } from "@configs/icons";

import { User } from "./User";
import { UserPrivateRequest } from "./UserPrivateRequest";

const GREY_TERTIARY = "var(--color-grey-tertiary)";

export const UsersList = () => {
	const [isOpenCollapsible, setIsOpenCollapsible] = useState(false);
	const privateRequestListQuery = usePrivateRequestListQuery();
	const broadcastStatusDataQuery = useBroadcastStatusDataQuery();
	const {
		usersData: { users, totalCount, guestsCount },
		spyCount,
	} = useWebSocketStore();
	const { MOBILE, TABLET } = useLayoutBreakpoint();

	const isMobile = MOBILE || TABLET;

	const hasPrivateRequests = (privateRequestListQuery?.length ?? 0) > 0;

	const handleOpenCollapsible = () => {
		setIsOpenCollapsible((prev) => !prev);
	};

	const UsersBox = ({ isOpenCollapsible = false }: { isOpenCollapsible?: boolean }) => (
		<div className="custom-scrollbar max-h-[calc(100vh-72px)] w-full overflow-y-auto">
			<div>
				{!isOpenCollapsible ? (
					<Typography size="sm" weight="bold" padding="none" className="mb-4">
						Private Show Requests: {privateRequestListQuery.length}
					</Typography>
				) : null}
				{!broadcastStatusDataQuery?.privateInfo && hasPrivateRequests ? (
					<Stack className="mb-4" wide spacing={8}>
						{privateRequestListQuery.map((user) => (
							<UserPrivateRequest key={user.id} user={user} />
						))}
					</Stack>
				) : null}
			</div>

			<Stack direction="row" spacing={12} align="center" className="mb-2">
				<Typography weight="bold" padding="none" size="sm">
					All Users: {totalCount > 0 ? totalCount : 0}
				</Typography>
				<Stack direction="row" spacing={2} align="center">
					<Image src={ICON_PATHS.SPY} alt="spy" />
					<Typography size="sm" weight="bold" padding="none">
						{spyCount}
					</Typography>
				</Stack>
			</Stack>

			<div className="w-full">
				{users.map((item) => (
					<div key={item?.userId} className="py-1.25">
						<User user={item} />
					</div>
				))}

				<Typography size="sm" color="msg_time" weight="bold" padding="none" className="mt-1.5">
					Guests: {guestsCount}
				</Typography>
			</div>
		</div>
	);

	if (isMobile) {
		return (
			<>
				<Collapsible open={isOpenCollapsible} onOpenChange={handleOpenCollapsible}>
					<CollapsibleTrigger
						className={cn(
							"flex h-10 w-full cursor-pointer items-center justify-between bg-block shadow-[0_15px_15px_5px_#101010]",
							isOpenCollapsible ? "rounded-b-0 rounded-t-lg pr-2 pl-4" : "rounded-lg px-2",
						)}
					>
						{isOpenCollapsible ? (
							<Typography size="sm" weight="bold" padding="none">
								Private show requests: {privateRequestListQuery.length}
							</Typography>
						) : (
							<Stack direction="row" spacing={12} align="center">
								<Badge className="text-sm font-bold" variant="primary">
									Requests: {privateRequestListQuery.length}
								</Badge>
								<Typography color="text" size="sm" weight="bold" padding="none">
									Users: {totalCount > 0 ? totalCount : 0}
								</Typography>
								<Stack direction="row" align="center" spacing={2}>
									<Image src={ICON_PATHS.SPY} alt="spy" />
									<Typography color="text" weight="bold" size="sm" padding="none">
										{spyCount}
									</Typography>
								</Stack>
							</Stack>
						)}
						{isOpenCollapsible ? (
							<ChevronUp color={GREY_TERTIARY} size={24} strokeWidth={1.5} />
						) : (
							<ChevronDown color={GREY_TERTIARY} size={24} strokeWidth={1.5} />
						)}
					</CollapsibleTrigger>
					<CollapsibleContent className="rounded-t-0 rounded-b-lg bg-block px-4 py-2.5">
						<Stack>
							<UsersBox isOpenCollapsible={isOpenCollapsible} />
						</Stack>
					</CollapsibleContent>
				</Collapsible>
			</>
		);
	}

	return <UsersBox />;
};
